import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePeriod } from '../../contexts/PeriodContext';
import { formatIdentity } from '../../lib/identity';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  LayoutDashboard, Users, CreditCard, Tag, Building2,
  Calendar, Upload, Table2, ListTodo, AlertTriangle,
  CheckSquare, Sliders, RefreshCw, Download, BarChart3,
  FileText, ChevronLeft, ChevronRight, LogOut, UserCircle,
  Menu, Settings2, TrendingUp
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard',   label: 'Dashboard',       icon: LayoutDashboard, roles: ['finance_admin','finance_reviewer','cardholder','auditor'] },
    ]
  },
  {
    label: 'Administration',
    items: [
      { path: '/users',       label: 'Users',           icon: Users,         roles: ['finance_admin'] },
      { path: '/cardholders', label: 'Cardholders',     icon: Users,         roles: ['finance_admin'] },
      { path: '/cards',       label: 'Cards',           icon: CreditCard,    roles: ['finance_admin'] },
      { path: '/categories',  label: 'Categories',      icon: Tag,           roles: ['finance_admin'] },
      { path: '/cost-centers',label: 'Cost Centers',    icon: Building2,     roles: ['finance_admin'] },
      { path: '/periods',     label: 'Periods',         icon: Calendar,      roles: ['finance_admin'] },
      { path: '/settings',    label: 'Settings',        icon: Settings2,     roles: ['finance_admin'] },
    ]
  },
  {
    label: 'Import & Transactions',
    items: [
      { path: '/import',         label: 'Import',         icon: Upload,        roles: ['finance_admin'] },
      { path: '/workbench',      label: 'Workbench',      icon: Table2,        roles: ['finance_admin','finance_reviewer'] },
      { path: '/my-transactions',label: 'My Transactions',icon: ListTodo,      roles: ['finance_admin','cardholder'] },
      { path: '/exceptions',     label: 'Exceptions',     icon: AlertTriangle, roles: ['finance_admin','finance_reviewer'] },
    ]
  },
  {
    label: 'Review & Close',
    items: [
      { path: '/approvals',          label: 'Approvals',       icon: CheckSquare,  roles: ['finance_admin','finance_reviewer'] },
      { path: '/period-progress',    label: 'Period Progress', icon: TrendingUp,   roles: ['finance_admin','finance_reviewer'] },
      { path: '/rules',              label: 'Rules',           icon: Sliders,      roles: ['finance_admin'] },
      { path: '/recurring-patterns', label: 'Recurring',       icon: RefreshCw,    roles: ['finance_admin','finance_reviewer'] },
      { path: '/exports',            label: 'Exports',         icon: Download,     roles: ['finance_admin'] },
    ]
  },
  {
    label: 'Reporting',
    items: [
      { path: '/analytics', label: 'Analytics',  icon: BarChart3, roles: ['finance_admin','finance_reviewer','auditor'] },
      { path: '/audit',     label: 'Audit Log',  icon: FileText,  roles: ['finance_admin','finance_reviewer','auditor'] },
    ]
  },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { periods, activePeriod, setActivePeriod } = usePeriod();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const filteredSections = NAV_SECTIONS.map(sec => ({
    ...sec,
    items: sec.items.filter(item => item.roles.includes(role))
  })).filter(sec => sec.items.length > 0);

  const ROLE_LABEL = {
    finance_admin: 'Finance Admin',
    finance_reviewer: 'Reviewer',
    cardholder: 'Cardholder',
    auditor: 'Auditor',
  };

  const ROLE_COLOR = {
    finance_admin: 'bg-blue-600',
    finance_reviewer: 'bg-amber-600',
    cardholder: 'bg-teal-600',
    auditor: 'bg-slate-600',
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar flex-shrink-0 flex flex-col h-full z-40 transition-all duration-200
          ${collapsed ? 'w-14' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          fixed lg:relative
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-800">
          {!collapsed && (
            <div>
              <div className="text-white font-bold text-sm leading-tight">Card Recon</div>
              <div className="text-slate-400 text-xs">Pam Golding</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2" data-testid="sidebar-nav">
          {filteredSections.map((sec) => (
            <div key={sec.label}>
              {!collapsed && (
                <div className="sidebar-section-label">{sec.label}</div>
              )}
              {sec.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                    data-testid={`nav-${item.path.replace('/', '').replace(/\//g, '-')}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-slate-800 p-3">
          {!collapsed ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
                  <UserCircle size={16} className="text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white truncate">{user?.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{formatIdentity(user)}</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full text-white ${ROLE_COLOR[role] || 'bg-slate-600'}`}>
                  {ROLE_LABEL[role] || role}
                </span>
                <button
                  onClick={handleLogout}
                  className="p-1 rounded text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors"
                  title="Logout"
                  data-testid="logout-btn"
                >
                  <LogOut size={14} />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex justify-center p-1.5 rounded text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex items-center justify-between px-4 py-3 bg-white border-b shadow-sm flex-shrink-0 h-14">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded hover:bg-slate-100 text-slate-600"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={18} />
            </button>
            {/* Period selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 hidden sm:block">Period:</span>
              <Select
                value={activePeriod?.id || ''}
                onValueChange={(val) => {
                  const p = periods.find(p => p.id === val);
                  if (p) setActivePeriod(p);
                }}
              >
                <SelectTrigger
                  className="h-7 text-xs w-44 border-slate-200"
                  data-testid="dashboard-period-selector"
                >
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {periods.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="flex items-center gap-2">
                        <span>{p.label}</span>
                        <span className={`text-[9px] font-semibold px-1 rounded chip-${p.status?.toLowerCase()}`}>
                          {p.status}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full text-white ${ROLE_COLOR[role] || 'bg-slate-600'}`}>
              {ROLE_LABEL[role] || role}
            </span>
            <div className="text-xs text-slate-600 hidden sm:block">{user?.name}</div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <div className="page-enter">{children}</div>
        </main>
      </div>
    </div>
  );
}
